import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { jwtDecode } from 'jwt-decode';
import { DateTime } from 'luxon';
import {
  Observable,
  catchError,
  map,
  race,
  switchMap,
  take,
  throwError,
} from 'rxjs';
import { environment } from '../../environments/environment';
import { AppState } from '../app.reducer';
import { AuthTokenPayload } from '../interfaces/auth-token';
import {
  logout,
  refreshToken,
  refreshTokenFailure,
  refreshTokenSuccess,
} from '../store/auth/auth.actions';
import { selectToken } from '../store/auth/auth.reducer';

@Injectable()
export class AuthenticationInterceptor implements HttpInterceptor {
  private isRefreshing = false;

  private store = inject(Store<AppState>);
  private actions$ = inject(Actions);

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (!request.url.startsWith(environment.apiBaseUrl)) {
      return next.handle(request);
    }

    if (request.url.startsWith(`${environment.apiBaseUrl}/company/auth`)) {
      return next.handle(request.clone({ withCredentials: true }));
    }

    return this.store.select(selectToken).pipe(
      take(1),
      switchMap((token) => {
        if (this.tokenExpired(token)) {
          return this.refreshTokenAndRetry(request, next);
        }

        return next.handle(this.addTokenToRequest(request, token)).pipe(
          catchError((error) => {
            if (error.status === 401) {
              return this.refreshTokenAndRetry(request, next).pipe(
                catchError((refreshError) => {
                  if (refreshError.status === 401) {
                    this.store.dispatch(logout());
                  }
                  return throwError(() => refreshError);
                })
              );
            }
            return throwError(() => error);
          })
        );
      })
    );
  }

  refreshTokenAndRetry(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (this.isRefreshing) {
      // If already refreshing, wait for the refresh to complete
      return this.waitForTokenRefresh().pipe(
        switchMap((token) => {
          if (token && !this.tokenExpired(token)) {
            return next.handle(this.addTokenToRequest(request, token));
          }
          return throwError(() => new Error('No valid token available'));
        })
      );
    }

    this.isRefreshing = true;
    const isPollingRequest =
      request.method === 'GET' &&
      (request.url.includes('notifications') ||
        request.url.includes('todos') ||
        request.url.includes('messages'));

    this.store.dispatch(refreshToken({ polling: isPollingRequest }));

    return this.waitForTokenRefresh().pipe(
      switchMap((token) => {
        this.isRefreshing = false;
        if (token && !this.tokenExpired(token)) {
          return next.handle(this.addTokenToRequest(request, token));
        }
        return throwError(() => new Error('Token refresh failed'));
      }),
      catchError((error) => {
        this.isRefreshing = false;
        return throwError(() => error);
      })
    );
  }

  private waitForTokenRefresh(): Observable<string> {
    return race(
      this.actions$.pipe(
        ofType(refreshTokenSuccess),
        take(1),
        map((action) => action.token)
      ),
      this.actions$.pipe(
        ofType(refreshTokenFailure),
        take(1),
        switchMap(() => throwError(() => new Error('Token refresh failed')))
      )
    );
  }

  addTokenToRequest(request: HttpRequest<unknown>, token: string) {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
      withCredentials: true,
    });
  }

  tokenExpired(token: string): boolean {
    if (!token) return true;
    try {
      const decoded = jwtDecode<AuthTokenPayload>(token);
      return decoded.exp <= Math.floor(DateTime.now().toSeconds());
    } catch (error) {
      console.warn('Failed to decode token:', error);
      return true;
    }
  }
}
